import { localStore, readSetting, writeSetting, type SettingsStore } from "./settings.ts";

/**
 * The viewer's "Effects" choice (ADR-141 question 4, decision F15): Full draws every particle effect, Reduced halves every
 * emitter's rate and the view's caps, Off draws no particles. The stored text is the value.
 */
export type EffectsChoice = "full" | "reduced" | "off";

/**
 * The storage key the Effects choice lives under.
 */
export const EFFECTS_KEY = "odyssey.effects";

/**
 * Reads a stored value as the Effects choice.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The choice, or `full` for anything that is not exactly `reduced` or `off`.
 */
export function parseEffects(raw: string | null): EffectsChoice {
  return raw === "reduced" || raw === "off" ? raw : "full";
}

/**
 * Reads the viewer's Effects choice.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns The stored choice, or `full` when none is stored, it is not one of the choices or the store cannot be read.
 */
export function readEffects(store: SettingsStore | null = localStore()): EffectsChoice {
  return readSetting(EFFECTS_KEY, parseEffects, store);
}

/**
 * Keeps the viewer's Effects choice.
 *
 * @param choice The choice.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeEffects(choice: EffectsChoice, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(EFFECTS_KEY, choice, store);
}

/**
 * The page's Effects choice, held as `ReducedMotionSetting` holds reduced motion: made from the choice the store holds as the
 * page loads, read by every surface each frame, and taking each choice Settings makes (or another tab makes: `refresh`),
 * which applies at once and is kept.
 *
 * @example
 * const effects = new EffectsSetting(readEffects());
 * effects.setChoice("reduced");
 */
export class EffectsSetting {
  private current: EffectsChoice;

  /**
   * @param choice The choice the page is made with, as `readEffects` gives it.
   * @param store Where a new choice is kept; this browser's `localStorage` when omitted.
   */
  public constructor(
    choice: EffectsChoice,
    private readonly store: SettingsStore | null = localStore(),
  ) {
    this.current = choice;
  }

  /**
   * The viewer's choice now.
   *
   * @returns The choice.
   */
  public get choice(): EffectsChoice {
    return this.current;
  }

  /**
   * Takes the viewer's choice and keeps it.
   *
   * @param choice The choice.
   */
  public setChoice(choice: EffectsChoice): void {
    this.current = choice;
    writeEffects(choice, this.store);
  }

  /**
   * Reads the stored choice again, for a change made in another tab.
   */
  public refresh(): void {
    this.current = readEffects(this.store);
  }
}
